// src/firebase/uploadMedia.js
import { storage } from './firebaseConfig';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

/**
 * Upload a file to Firebase Storage.
 *
 * Default path:
 *   events/{eventId}/{uid}/{timestamp}_{sanitizedName}
 *
 * Optional override:
 *   If opts.storagePath is provided, upload EXACTLY to that storage path.
 *   Example:
 *     events/{eventId}/boomerang_frames/{mediaId}/frame_000.jpg
 *
 * REQUIREMENTS (matches your Storage rules):
 * - You MUST pass the authenticated user's uid via opts.uid
 * - Caller should be signed in (anon or Google)
 */
export default async function uploadMedia(eventId, file, opts = {}) {
  const {
    uid,
    onProgress,
    onComplete,
    onError,
    metadata: extraMetadata = {},
    storagePath,
    maxRetries = 1,
  } = opts;

  if (!eventId) throw new Error('uploadMedia(eventId, file, opts): eventId is required.');
  if (!file) throw new Error('uploadMedia(eventId, file, opts): file is required.');
  if (!uid) throw new Error('uploadMedia(eventId, file, opts): opts.uid (current user UID) is required to satisfy Storage rules.');

  const name = sanitizeFilename(file.name || 'file');

  const path =
    typeof storagePath === 'string' && storagePath.trim().length > 0
      ? storagePath.trim()
      : `events/${eventId}/${uid}/${Date.now()}_${name}`;

  const metadata = {
    contentType: file.type || 'application/octet-stream',
    ...extraMetadata,
  };

  let lastError = null;

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      return await uploadMediaOnce({
        file,
        path,
        metadata,
        onProgress,
        onComplete,
        onError,
      });
    } catch (err) {
      lastError = err;

      if (attempt >= maxRetries || !isRetryableUploadError(err)) {
        throw err;
      }

      await wait(700 * (attempt + 1));
    }
  }

  throw lastError;
}

/* ---------------- helpers ---------------- */

function uploadMediaOnce({ file, path, metadata, onProgress, onComplete, onError }) {
  const fileRef = ref(storage, path);
  const task = uploadBytesResumable(fileRef, file, metadata);

  return new Promise((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => {
        try {
          onProgress?.(snap);
        } catch {}
      },
      (err) => {
        try {
          onError?.(err);
        } catch {}
        reject(err);
      },
      async () => {
        try {
          onComplete?.();
          const url = await getDownloadURL(task.snapshot.ref);
          resolve(url);
        } catch (e) {
          reject(e);
        }
      }
    );
  });
}

function isRetryableUploadError(error) {
  const code = String(error?.code || '').toLowerCase();
  const message = String(error?.message || '').toLowerCase();

  return (
    code.includes('storage/retry-limit-exceeded') ||
    code.includes('storage/canceled') ||
    code.includes('storage/unknown') ||
    code.includes('storage/quota-exceeded') ||
    message.includes('network') ||
    message.includes('timeout') ||
    message.includes('failed to fetch')
  );
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function sanitizeFilename(raw) {
  const dotSafe = raw.replace(/\s+/g, '_').replace(/[^\w.\-]/g, '_');
  return dotSafe.length ? dotSafe : 'file';
}